<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { useAmplitude } from '@/composables/use-amplitude'
import OnboardingRetentionLayout from '@/pages/onboarding-retention/components/OnboardingRetentionLayout.vue'
import { useConversionFeedList } from '@/services/api/ads.api'
import {
  RETENTION_ESP_META,
  useOnboardingRetentionProgressStore,
} from '@/stores/onboarding-retention-progress'

definePage({
  path: '/onboarding-retention/step2',
  meta: { layout: false },
})

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { track } = useAmplitude()

const KlaviyoConversionFeedPage = defineAsyncComponent(() => import('@/pages/conversion-feed/klaviyo-embed.vue'))
const OmnisendConversionFeedPage = defineAsyncComponent(() => import('@/pages/conversion-feed/omnisend-embed.vue'))
const CartseeConversionFeedPage = defineAsyncComponent(() => import('@/pages/conversion-feed/cartsee-embed.vue'))
const ManbaConversionFeedPage = defineAsyncComponent(() => import('@/pages/conversion-feed/manba-embed.vue'))

const progressStore = useOnboardingRetentionProgressStore()
const { progress, loaded, resolvedEsp, selectedEspMeta } = storeToRefs(progressStore)

const i18nParams = computed(() => ({ esp: selectedEspMeta.value?.displayName ?? t('onboardingRetention.espBrandNames.klaviyo') }))

const accountDisconnectedCss = computed(() => `"${t('onboardingRetention.step2.accountDisconnected')}"`)
const accountSelectDescCss = computed(() => `"${t('onboardingRetention.step2.accountSelectDesc', i18nParams.value)}"`)

const EspConversionFeedPage = computed(() => {
  const esp = resolvedEsp.value ?? 'klaviyo'
  if (esp === 'klaviyo')
    return KlaviyoConversionFeedPage
  if (esp === 'omnisend')
    return OmnisendConversionFeedPage
  if (esp === 'manba')
    return ManbaConversionFeedPage
  return CartseeConversionFeedPage
})

const espAdType = computed(() => {
  const esp = resolvedEsp.value ?? 'klaviyo'
  return RETENTION_ESP_META[esp].adType
})

const saving = ref(false)
const { queryFn: getConversionFeedList } = useConversionFeedList()
const conversionFeedList = ref<any[]>([])
const feedCheckTimer = ref<number | null>(null)
const feedEmbedRef = ref<HTMLElement | null>(null)
const embeddedSaveObserver = ref<MutationObserver | null>(null)
const canContinueFromEmbed = ref(false)

const progressData = computed<Record<string, any>>(() => progress.value?.progress_data || {})

const platformFeeds = computed(() => conversionFeedList.value.filter(item => item.ad_type === espAdType.value))
const hasConversionFeed = computed(() => platformFeeds.value.length > 0 || progressData.value.step2ConversionFeedConfigured === true)
const firstFeedId = computed<number | undefined>(() => {
  const id = platformFeeds.value[0]?.id
  return typeof id === 'number' ? id : undefined
})
const canContinue = computed(() => hasConversionFeed.value || canContinueFromEmbed.value)

const completedSteps = computed(() => {
  const out: number[] = []
  if (progressData.value.step1Connected === true)
    out.push(1)
  if (hasConversionFeed.value)
    out.push(2)
  if (progressData.value.step3AppEmbedEnabled === true)
    out.push(3)
  if (progress.value?.is_completed)
    out.push(4)
  return out
})

function getEmbeddedSaveButton() {
  return feedEmbedRef.value?.querySelector('.flex.justify-end.gap-4.mt-6.pt-6.border-t button:last-of-type') as HTMLButtonElement | null
}

function syncContinueAvailability() {
  const saveButton = getEmbeddedSaveButton()
  canContinueFromEmbed.value = Boolean(saveButton) && !saveButton?.disabled
}

function stopObservingEmbeddedSaveButton() {
  embeddedSaveObserver.value?.disconnect()
  embeddedSaveObserver.value = null
}

function observeEmbeddedSaveButton() {
  stopObservingEmbeddedSaveButton()
  if (!feedEmbedRef.value)
    return

  syncContinueAvailability()

  const observer = new MutationObserver(() => {
    syncContinueAvailability()
  })

  observer.observe(feedEmbedRef.value, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'class', 'aria-disabled'],
  })

  embeddedSaveObserver.value = observer
}

async function ensureLoaded() {
  if (loaded.value)
    return
  let result: any = null
  try {
    result = await progressStore.loadFromBackend()
  }
  catch (e) {
    console.warn('[retention-onboarding step2] loadFromBackend failed, fallback local:', e)
  }
  if (!resolvedEsp.value) {
    if (result !== null) {
      router.replace('/onboarding-retention/step0')
      return
    }
    progressStore.setEsp('klaviyo')
    return
  }
  if (result === 'completed') {
    router.replace('/onboarding-retention/step4')
  }
}

async function saveProgress(nextStep: number, patch: Record<string, any>) {
  const merged = {
    ...(progress.value?.progress_data || {}),
    ...patch,
  }
  await progressStore.save({
    current_step: nextStep,
    current_platform: 'retention',
    progress_data: merged,
  })
  if (progress.value) {
    progress.value = {
      ...progress.value,
      current_step: nextStep,
      current_platform: 'retention',
      progress_data: merged,
    }
  }
}

async function loadConversionFeeds() {
  try {
    const response = await getConversionFeedList({ page: 1, page_size: 100 })
    if (response.code > 0 && response.data?.records)
      conversionFeedList.value = response.data.records
  }
  catch {}
}

async function waitForConversionFeedConfigured(timeoutMs = 15_000, intervalMs = 500) {
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeoutMs) {
    await loadConversionFeeds()
    if (platformFeeds.value.length > 0)
      return true

    await new Promise(resolve => window.setTimeout(resolve, intervalMs))
  }

  return false
}

async function next() {
  if (saving.value)
    return
  track('retention_onboarding_step2_continue_click', { esp: resolvedEsp.value ?? 'klaviyo' })
  saving.value = true
  try {
    await loadConversionFeeds()

    if (!hasConversionFeed.value) {
      const saveButton = getEmbeddedSaveButton()
      if (!saveButton || saveButton.disabled)
        return

      saveButton.click()

      const configured = await waitForConversionFeedConfigured()
      syncContinueAvailability()
      if (!configured)
        return
    }

    await saveProgress(3, {
      step2ConversionFeedConfigured: true,
      step2FeedId: firstFeedId.value ?? progressData.value.step2FeedId,
    })
    router.push({ path: '/onboarding-retention/step3', query: { prev: route.fullPath } })
  }
  finally {
    saving.value = false
  }
}

function back() {
  track('retention_onboarding_step2_back_click')
  const prev = route.query?.prev as string | undefined
  if (prev)
    router.push(prev)
  else router.push('/onboarding-retention/step1')
}

onBeforeRouteLeave((to) => {
  if (to.path.startsWith('/onboarding-retention'))
    return true
  if (to.path.startsWith('/integrations/')) {
    router.push({ path: '/onboarding-retention/step1', query: { prev: route.fullPath } })
    return false
  }
  loadConversionFeeds()
  return false
})

watch(
  () => progressData.value.step1Connected,
  (v, oldV) => {
    if (v && v !== oldV)
      loadConversionFeeds()
  },
)

function startFeedCheckLoop() {
  if (feedCheckTimer.value)
    return
  feedCheckTimer.value = window.setInterval(async () => {
    if (!hasConversionFeed.value) {
      await loadConversionFeeds()
    }
  }, 3000)
}

function stopFeedCheckLoop() {
  if (feedCheckTimer.value) {
    clearInterval(feedCheckTimer.value)
    feedCheckTimer.value = null
  }
}

watch(hasConversionFeed, (val) => {
  if (val) {
    stopFeedCheckLoop()
  }
  syncContinueAvailability()
})

onMounted(async () => {
  track('retention_onboarding_step2_pageview', { esp: resolvedEsp.value ?? 'klaviyo' })
  await ensureLoaded()
  if (!resolvedEsp.value)
    return
  await loadConversionFeeds()
  await nextTick()
  observeEmbeddedSaveButton()
  startFeedCheckLoop()
})

onUnmounted(() => {
  stopFeedCheckLoop()
  stopObservingEmbeddedSaveButton()
})
</script>

<template>
  <OnboardingRetentionLayout
    :current-step="2"
    :completed-steps="completedSteps"
    title-key="onboardingRetention.step2.title"
    subtitle-key="onboardingRetention.step2.subtitle"
  >
    <div class="space-y-4">
      <div class="rounded-[var(--p-border-radius-200)] bg-white border border-black overflow-auto p-6">
        <div v-if="hasConversionFeed" class="p-2 flex items-center justify-end gap-3">
          <div class="inline-flex items-center gap-2 text-sm text-emerald-700">
            <Icon name="lucide:check-circle-2" class="h-4 w-4" />
            <span>{{ t('onboardingRetention.status.configured') }}</span>
          </div>
        </div>
        <div
          ref="feedEmbedRef"
          class="onboarding-retention-step2-feed-embed p-6 overflow-auto"
          data-testid="onboarding-retention-step2-feed"
          :style="{ '--account-disconnected-msg': accountDisconnectedCss, '--account-select-desc': accountSelectDescCss }"
        >
          <component :is="EspConversionFeedPage" :default-guide-card="false" />
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 pt-2">
        <Button variant="outline" class="bg-white border-black text-black shadow-none hover:bg-white" @click="back">
          {{ t('onboardingRetention.actions.back') }}
        </Button>
        <Button
          :disabled="!canContinue || saving"
          :class="(canContinue && !saving) ? 'bg-black hover:bg-black/90 shadow-none' : 'bg-[#6D6D6D] hover:bg-[#5d5d5d] shadow-none'"
          @click="next"
        >
          <Icon v-if="saving" name="lucide:loader-2" class="h-4 w-4 mr-2 animate-spin" />
          {{ t('onboardingRetention.actions.continue') }}
        </Button>
      </div>
    </div>

    <template #right>
      <div class="h-full w-full bg-[#F5F4ED]">
        <div class="px-8 pt-8 pb-8">
          <div class="text-[10px] font-semibold tracking-[0.18em] text-[#9CA3AF]">
            {{ t('onboardingRetention.step2.valueCard.kicker') }}
          </div>
          <div class="mt-2 text-[20px] leading-tight font-semibold text-[#111827]">
            {{ t('onboardingRetention.step2.valueCard.title', i18nParams) }}
          </div>

          <div class="mt-6 overflow-hidden rounded-[var(--p-border-radius-300)] bg-[#0B0F14]">
            <div class="px-6 pt-6 pb-5 text-[14px] leading-snug text-[#A1A1AA]">
              <div class="text-[12px] tracking-[0.18em] text-[#6B7280] uppercase">
                {{ t('onboardingRetention.step2.valueCard.brand') }}
              </div>
              <div class="mt-3">
                {{ t('onboardingRetention.step2.valueCard.brandDesc') }}
              </div>
            </div>
            <div class="h-px w-full bg-white/10" />
            <div class="px-6 pt-5 pb-5">
              <div class="text-[12px] tracking-[0.18em] text-[#71717A]">
                {{ t('onboardingRetention.step2.valueCard.metricLabel') }}
              </div>
              <div class="mt-2 flex items-end justify-between gap-4">
                <div class="text-[38px] leading-none font-semibold text-white">
                  {{ t('onboardingRetention.step2.valueCard.metricValue') }}
                </div>
                <div class="text-[22px] leading-none font-semibold text-[#22C55E]">
                  {{ t('onboardingRetention.step2.valueCard.metricDelta') }}
                </div>
              </div>
            </div>
            <div class="h-px w-full bg-white/10" />
            <div class="px-6 pt-5 pb-6 flex items-start gap-6 text-[14px] text-[#A1A1AA]">
              <div class="w-[120px] space-y-1">
                <div class="text-[#71717A]">
                  {{ t('onboardingRetention.step2.valueCard.periodLabel') }}
                </div>
                <div class="text-white font-semibold">
                  {{ t('onboardingRetention.step2.valueCard.periodValue') }}
                </div>
              </div>
              <div class="flex-1 space-y-1 border-l border-white/10 pl-6">
                <div class="text-[#71717A]">
                  {{ t('onboardingRetention.step2.valueCard.vsLabel') }}
                </div>
                <div class="text-[#22C55E] font-semibold">
                  {{ t('onboardingRetention.step2.valueCard.vsValue') }}
                </div>
              </div>
            </div>
          </div>

          <div class="mt-8 space-y-3 text-[14px] leading-snug text-[#52525B]">
            <div class="flex gap-2">
              <span class="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span>{{ t('onboardingRetention.step2.right.point1', i18nParams) }}</span>
            </div>
            <div class="flex gap-2">
              <span class="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span>{{ t('onboardingRetention.step2.right.point2') }}</span>
            </div>
            <div class="flex gap-2">
              <span class="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span>{{ t('onboardingRetention.step2.right.point3') }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </OnboardingRetentionLayout>
</template>

<style scoped>
:deep(.mb-8) { display: none; }
:deep(.mb-8 .flex.items-center.gap-4 > button) { display: none; }
:deep(.mb-8 h1) { display: none; }
:deep(.mb-8 .mt-2.pl-6 p) { display: none; }
:deep(.max-w-4xl.mx-auto.mb-6) { display: none; }
:deep(.max-w-7xl.mx-auto) { max-width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }
:deep(.grid.max-w-4xl.mx-auto) { max-width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; }
:deep(div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t)) { margin-left: 0 !important; margin-right: 0 !important; box-shadow: none !important; border-width: 0 !important; padding: 10px !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div.mt-6.p-4.rounded-lg) { display: none !important; }
:deep([data-testid="onboarding-retention-step2-feed"] p.text-muted-foreground.mb-4.text-left) { font-size: 0 !important; }
:deep([data-testid="onboarding-retention-step2-feed"] p.text-muted-foreground.mb-4.text-left)::before { content: var(--account-disconnected-msg); font-size: 15px; line-height: 1.35; color: #6D6D6D; display: block; }

:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) .mb-8) { margin-bottom: 8px !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) h2.text-xl.font-semibold.mb-2) { font-size: 16px !important; margin-bottom: 4px !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) .space-y-8 > *) :not([hidden]) ~ :not([hidden]) { margin-top: 14px !important; }

:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) .flex.items-start.gap-4) { gap: 12px !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) .w-8.h-8.rounded-full.flex.items-center.justify-center.flex-shrink-0.mt-1) { margin-top: 0 !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) h3.text-lg.font-semibold.mb-3) { margin-bottom: 4px !important; font-size: 16px !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) p.text-muted-foreground.mb-6) { margin-bottom: 8px !important; font-size: 15px !important; line-height: 1.35 !important; }

:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) .space-y-8 > *):nth-child(2) p.text-muted-foreground.mb-6 { font-size: 0 !important; }
:deep([data-testid="onboarding-retention-step2-feed"] div:has(> .flex.justify-end.gap-4.mt-6.pt-6.border-t) .space-y-8 > *):nth-child(2) p.text-muted-foreground.mb-6::before { content: var(--account-select-desc); font-size: 15px; line-height: 1.35; color: #6D6D6D; display: block; }
:deep([data-testid="onboarding-retention-step2-feed"] .flex.justify-end.gap-4.mt-6.pt-6.border-t) { display: none !important; }
:deep([data-testid="onboarding-retention-step2-feed"] .flex.justify-end.gap-4.mt-6.pt-6.border-t > *) { flex-shrink: 0; }
</style>
